import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Row, Space, Spin, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import ReportVerificationService, {
  ControlTaskReportVerification,
} from '../../services/ReportVerificationService';
import kontrolxLogo from '../../assets/auth/kontrolxlogo.svg';
import './ReportVerificationPage.scss';

const { Text, Title } = Typography;

const displayValue = (value?: string | null) => {
  const normalized = value?.trim();
  return normalized || '-';
};

const resolveDownloadFileName = (
  report: ControlTaskReportVerification | null,
  contentDisposition?: string,
) => {
  const headerFileName = contentDisposition
    ?.split(';')
    .map((part) => part.trim())
    .find((part) => part.toLowerCase().startsWith('filename='))
    ?.replace(/^filename=/i, '')
    .replace(/^"|"$/g, '');

  return headerFileName || report?.fileName || 'rapor.pdf';
};

const ReportVerificationPage: React.FC = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [report, setReport] = useState<ControlTaskReportVerification | null>(null);
  const [message, setMessage] = useState('');

  const reportPublicId = useMemo(() => {
    const normalizedId = id?.trim();
    return normalizedId || undefined;
  }, [id]);

  useEffect(() => {
    if (!reportPublicId) {
      setLoading(false);
      setMessage('Geçersiz rapor doğrulama bağlantısı.');
      return;
    }

    setLoading(true);
    ReportVerificationService.getReportVerification(reportPublicId)
      .then((response) => {
        setReport(response.data);
        setMessage('');
      })
      .catch((error) => {
        setReport(null);
        setMessage(error?.response?.data?.message || 'Rapor doğrulama bilgisi bulunamadı.');
      })
      .finally(() => setLoading(false));
  }, [reportPublicId]);

  const handleDownload = async () => {
    if (!reportPublicId) {
      return;
    }

    let objectUrl: string | null = null;
    try {
      setDownloading(true);
      const response = await ReportVerificationService.downloadReportPdf(reportPublicId);
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/pdf' });
      objectUrl = URL.createObjectURL(blob);

      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = resolveDownloadFileName(report, response.headers?.['content-disposition']);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Rapor indirilemedi.');
    } finally {
      if (objectUrl) {
        window.setTimeout(() => URL.revokeObjectURL(objectUrl as string), 60_000);
      }
      setDownloading(false);
    }
  };

  return (
    <div className="report-verification-page">
      <Row justify="center" align="middle" className="report-verification-page__row">
        <Col xs={23} sm={20} md={14} lg={10} xl={7}>
          <div className="report-verification-page__logo-wrap">
            <img src={kontrolxLogo} alt="KONTROLX Logo" className="report-verification-page__logo" />
          </div>
          <Card className="report-verification-page__card">
            <Title level={4} className="report-verification-page__title">
              Muayene Doğrulama
            </Title>

            {loading ? (
              <Space size={12}>
                <Spin />
                <Text>Rapor doğrulama bilgisi yükleniyor...</Text>
              </Space>
            ) : (
              <Space direction="vertical" size={14} className="report-verification-page__content">
                {message && (
                  <Alert
                    type={report ? 'warning' : 'error'}
                    showIcon
                    message={report ? 'Bilgilendirme' : 'Rapor Bulunamadı'}
                    description={message}
                  />
                )}

                {report && (
                  <>
                    <div className="report-verification-page__field">
                      <Text strong>Firma</Text>
                      <Text className="report-verification-page__value">{displayValue(report.companyName)}</Text>
                    </div>
                    <div className="report-verification-page__field">
                      <Text strong>Ekipman Adı</Text>
                      <Text className="report-verification-page__value">{displayValue(report.equipmentName)}</Text>
                    </div>
                    <div className="report-verification-page__field">
                      <Text strong>Ekipman Kodu</Text>
                      <Text className="report-verification-page__value">{displayValue(report.equipmentCode)}</Text>
                    </div>
                    <div className="report-verification-page__field">
                      <Text strong>Rapor No</Text>
                      <Text className="report-verification-page__value">{displayValue(report.reportNumber)}</Text>
                    </div>
                    <div className="report-verification-page__field">
                      <Text strong>Muayene Tarihi</Text>
                      <Text className="report-verification-page__value">{displayValue(report.inspectionDate)}</Text>
                    </div>
                    <div className="report-verification-page__field">
                      <Text strong>Gelecek Muayene Tarihi</Text>
                      <Text className="report-verification-page__value">{displayValue(report.nextInspectionDate)}</Text>
                    </div>
                    <div className="report-verification-page__field">
                      <Text strong>Muayene Sorumlusu</Text>
                      <Text className="report-verification-page__value">{displayValue(report.inspectorName)}</Text>
                    </div>
                    <div className="report-verification-page__field">
                      <Text strong>Rapor Linki</Text>
                      <Button
                        type="link"
                        className="report-verification-page__link-button"
                        onClick={handleDownload}
                        loading={downloading}
                      >
                        Raporu İndir
                      </Button>
                    </div>
                  </>
                )}
              </Space>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ReportVerificationPage;
